/**
 * Helpers para leer datos de producto en formato raw (NewProduct) con valores por defecto
 * para mantener la UI sin errores cuando falte compatibilidad.
 */
import type {
    NewProductInList,
    NewProductSubcategoryValue
} from '../types/newProductDisplay'

const DEFAULT_STR = '-'
const DEFAULT_NUM = 0
const DEFAULT_ARR: unknown[] = []

type ProductListLegacyFields = {
    description?: string | null
    name?: string | null
}

function getDescriptionFromDetails(
    p: NewProductInList | null | undefined
): string {
    if (!p) return DEFAULT_STR
    const typeId = p.type_id
    if (typeId === 0 && p.equipment_details?.description)
        return String(p.equipment_details.description)
    if (typeId === 1 && p.consumable_details?.description)
        return String(p.consumable_details.description)
    if (typeId === 2 && p.service_details?.description)
        return String(p.service_details.description)
    const nested =
        p.equipment_details?.description ??
        p.consumable_details?.description ??
        p.service_details?.description
    if (nested != null && String(nested).trim() !== '') return String(nested)
    const legacy =
        (p as ProductListLegacyFields).description ??
        (p as ProductListLegacyFields).name
    if (legacy != null && String(legacy).trim() !== '') return String(legacy)
    return DEFAULT_STR
}

export function getProductCode(p: NewProductInList | null | undefined): string {
    if (p?.id != null) return `NP-${p.id}`
    return DEFAULT_STR
}

export function getProductSkuDisplay(
    p: NewProductInList | null | undefined
): string {
    if (!p) return DEFAULT_STR
    const raw = p.sku_display
    if (raw != null && String(raw).trim() !== '') return String(raw)
    return DEFAULT_STR
}

export function getProductName(p: NewProductInList | null | undefined): string {
    return getDescriptionFromDetails(p)
}

export function getProductDescription(
    p: NewProductInList | null | undefined
): string {
    return getDescriptionFromDetails(p)
}

export function getProductBrandName(
    p: NewProductInList | null | undefined
): string {
    if (!p) return DEFAULT_STR
    const nested = p.equipment_details?.brand ?? p.consumable_details?.brand
    const nestedName = nested?.name
    if (nestedName != null && String(nestedName).trim() !== '')
        return String(nestedName)
    const top = (p as { brand?: { name?: string | null } | null }).brand?.name
    if (top != null && String(top).trim() !== '') return String(top)
    return DEFAULT_STR
}

export function getProductSubcategoryName(
    p: NewProductInList | null | undefined
): string {
    return p?.subcategory?.name ?? DEFAULT_STR
}

export function getProductSubcategoryCategoryName(
    p: NewProductInList | null | undefined
): string {
    return p?.subcategory?.category?.name ?? DEFAULT_STR
}

export function getProductTypeProductName(
    p: NewProductInList | null | undefined
): string {
    return (
        p?.product_type?.name ??
        (p as { type_product?: { name?: string } })?.type_product?.name ??
        DEFAULT_STR
    )
}

export function getProductManufacturerCode(
    p: NewProductInList | null | undefined
): string {
    if (!p) return DEFAULT_STR
    return (
        p.equipment_details?.manufacturer_code ??
        p.consumable_details?.manufacturer_code ??
        p.service_details?.manufacturer_code ??
        DEFAULT_STR
    )
}

export function getProductModel(
    p: NewProductInList | null | undefined
): string {
    return p?.equipment_details?.equipment_model ?? DEFAULT_STR
}

export function getProductPresentation(): string {
    return DEFAULT_STR
}

export function getProductPresentationProducts(
    p: NewProductInList | null | undefined
): Array<{ group_presentation?: { name: string }; amount?: number }> {
    const arr = p?.presentation_products ?? DEFAULT_ARR
    return arr as Array<{
        group_presentation?: { name: string }
        amount?: number
    }>
}

/** amount del primer presentation_product o '' */
export function getProductPresentationAmount(
    p: NewProductInList | null | undefined
): string {
    const pres = getProductPresentationProducts(p)
    const first = pres[0]
    return first?.amount != null ? String(first.amount) : ''
}

/** Ej: "Grupo (123)" o '-' si no hay presentación */
export function getProductPresentationLabel(
    p: NewProductInList | null | undefined
): string {
    const pres = getProductPresentationProducts(p)
    const first = pres[0]
    if (!first?.group_presentation?.name) return DEFAULT_STR
    const amount = first?.amount ?? DEFAULT_NUM
    return `${first.group_presentation.name} (${amount})`
}

export function getProductSubcategoryValues(
    p: NewProductInList | null | undefined
): NewProductSubcategoryValue[] {
    return (p?.subcategory?.subcategory_values ??
        DEFAULT_ARR) as NewProductSubcategoryValue[]
}

export function getProductBrandId(
    p: NewProductInList | null | undefined
): number | null {
    if (!p) return null
    const id = p.equipment_details?.brand_id ?? p.consumable_details?.brand_id
    return id ?? null
}
